import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../hooks/useApi';
import { useCheckout } from '../hooks/useCheckout';
import { useUserContext } from '../context/UserContext';

interface OfficeTier {
  id: number;
  tierName: string;
  monthlyPrice: number | null;
  maxUsers: number | null;
}

interface Market {
  id: number;
  name: string;
  stripePriceId: string | null;
  monthlyPrice: number | null;
  neighborhoodCount: number;
  hasAccess: boolean;
  hasMarketAccess: boolean;
  photoUrl: string | null;
  officeTiers: OfficeTier[];
  currentOfficeTierId: number | null;
}

const FONT: React.CSSProperties = { fontFamily: "'Montserrat', sans-serif" };

const formatSeats = (maxUsers: number | null) => (maxUsers == null ? 'Unlimited seats' : `Up to ${maxUsers} seats`);

export function StorefrontPage() {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { anonFetch, authFetch } = useApi();
  const { startCheckout } = useCheckout();
  const { me, refresh } = useUserContext();
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState<number | null>(null);
  const [checkingOutOfficeTierId, setCheckingOutOfficeTierId] = useState<number | null>(null);
  const [changingTierMarketId, setChangingTierMarketId] = useState<number | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState<{ market: Market; tier: OfficeTier } | null>(null);

  const loadMarkets = useCallback(() => {
    const doFetch = isAuthenticated ? authFetch : anonFetch;
    return doFetch('/api/markets').then(r => r.json()).then((data: Market[]) => setMarkets(data));
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setLoading(true);
    loadMarkets().catch(() => {}).finally(() => setLoading(false));
  }, [loadMarkets]);

  const handleSubscribe = async (market: Market) => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: '/account' } });
      return;
    }
    if (!market.stripePriceId) return;
    setCheckingOut(market.id);
    try {
      await startCheckout([{ type: 'market', id: market.id }]);
    } catch {
      setCheckingOut(null);
    }
  };

  const handleSubscribeOffice = async (tierId: number) => {
    if (!isAuthenticated) {
      loginWithRedirect({ appState: { returnTo: '/account' } });
      return;
    }
    setCheckingOutOfficeTierId(tierId);
    try {
      await startCheckout([{ type: 'office', id: tierId }]);
    } catch {
      setCheckingOutOfficeTierId(null);
    }
  };

  const handleChangeOfficeTier = async (marketId: number, tierId: number) => {
    setChangingTierMarketId(marketId);
    try {
      const res = await authFetch('/api/checkout/office-tier', {
        method: 'POST',
        body: JSON.stringify({ newTierId: tierId }),
      });
      if (res.ok) {
        await Promise.all([refresh(), loadMarkets()]);
      } else {
        const body = await res.json().catch(() => null);
        alert(typeof body === 'string' ? body : 'Could not change your Office Subscription tier.');
      }
    } finally {
      setChangingTierMarketId(null);
    }
  };

  const handleConfirmSwitch = async () => {
    if (!confirmSwitch) return;
    await handleChangeOfficeTier(confirmSwitch.market.id, confirmSwitch.tier.id);
    setConfirmSwitch(null);
  };

  const handleManageBilling = async () => {
    try {
      const res = await authFetch('/api/checkout/portal', { method: 'POST' });
      const { url } = await res.json();
      window.location.href = url;
    } catch {
      alert('Could not open billing portal. Please try again.');
    }
  };

  const hasPaidSub = me?.hasPaidSubscription ?? false;
  const showOfficeOptions = !me?.organization || !!me?.isOrgManager;

  const hasSubs = markets.some(m => m.hasMarketAccess && hasPaidSub);
  const sortedMarkets = [...markets].sort((a, b) => {
    const aActive = a.hasMarketAccess && hasPaidSub ? 0 : 1;
    const bActive = b.hasMarketAccess && hasPaidSub ? 0 : 1;
    return aActive - bActive;
  });
  const officeMarkets = markets.filter(m => showOfficeOptions && m.officeTiers.length > 0);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
      <div style={{ width: 40, height: 40, borderRadius: '50%', border: '4px solid #e0dbd7', borderTopColor: '#ba9394', animation: 'prism-spin 0.75s linear infinite' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2.5rem 2rem', ...FONT }}>

      <h1 style={{ fontSize: 28, fontWeight: 600, marginBottom: '0.25rem' }}>
        {hasSubs ? 'Your Subscription' : 'Get Started with Prism'}
      </h1>
      <p style={{ color: '#888', fontSize: 13, marginBottom: '2.5rem' }}>
        {hasSubs
          ? 'Manage your active markets or add new ones below.'
          : 'Choose a market to get started — your first month is free.'}
      </p>

      {sortedMarkets.length > 0 && (
        <section style={{ marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: '#333', margin: 0 }}>Individual Subscriptions</h2>
            {hasSubs && (
              <button
                onClick={handleManageBilling}
                style={{ background: 'none', border: 'none', color: '#ba9394', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline', ...FONT }}
              >
                Manage billing →
              </button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem' }}>
            {sortedMarkets.map(m => (
              <MarketCard
                key={m.id}
                market={m}
                mode={m.hasMarketAccess && hasPaidSub ? 'active' : 'subscribe'}
                checkingOut={checkingOut === m.id}
                onSubscribe={() => handleSubscribe(m)}
              />
            ))}
          </div>
          {!isAuthenticated && (
            <p style={{ fontSize: 13, color: '#888', marginTop: '1.5rem' }}>
              Already have an account?{' '}
              <button
                onClick={() => loginWithRedirect({ appState: { returnTo: '/account' } })}
                style={{ background: 'none', border: 'none', color: '#ba9394', cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline', ...FONT }}
              >
                Sign in
              </button>
            </p>
          )}
        </section>
      )}

      {markets.length === 0 && (
        <p style={{ color: '#888' }}>No markets available at this time.</p>
      )}

      {officeMarkets.length > 0 && (
        <section>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: '0.35rem' }}>Office Subscriptions</h2>
          <p style={{ fontSize: 12, color: '#888', marginTop: 0, marginBottom: '1.5rem' }}>
            Registering as an Organization Manager? Use your company email address when signing up.
          </p>
          {officeMarkets.map(m => (
            <div key={m.id} style={{ marginBottom: '1.75rem' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#666', marginBottom: '0.75rem' }}>{m.name}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem' }}>
                {m.officeTiers.map(tier => (
                  <OfficeTierCard
                    key={tier.id}
                    market={m}
                    tier={tier}
                    isCurrent={tier.id === m.currentOfficeTierId}
                    busy={checkingOutOfficeTierId === tier.id || changingTierMarketId === m.id}
                    onClick={() => (m.currentOfficeTierId != null
                      ? setConfirmSwitch({ market: m, tier })
                      : handleSubscribeOffice(tier.id))}
                  />
                ))}
              </div>
            </div>
          ))}
        </section>
      )}

      {confirmSwitch && (
        <OfficeTierSwitchModal
          market={confirmSwitch.market}
          tier={confirmSwitch.tier}
          activeMemberCount={me?.organization?.activeMemberCount ?? 0}
          busy={changingTierMarketId === confirmSwitch.market.id}
          onConfirm={handleConfirmSwitch}
          onClose={() => setConfirmSwitch(null)}
        />
      )}
    </div>
  );
}

type CardMode = 'active' | 'subscribe';

interface MarketCardProps {
  market: Market;
  mode: CardMode;
  checkingOut?: boolean;
  onSubscribe?: () => void;
}

function MarketCard({ market, mode, checkingOut, onSubscribe }: MarketCardProps) {
  const photoUrl = market.photoUrl ?? `https://prismassets.blob.core.windows.net/market-images/${market.id}.jpg`;

  return (
    <div style={{
      width: 260,
      border: '1px solid #ddd',
      borderRadius: 10,
      overflow: 'hidden',
      background: mode === 'active' ? '#fafafa' : '#fff',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        height: 150,
        backgroundImage: `url(${photoUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#e0dbd7',
      }} />
      <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>{market.name}</div>
        <div style={{ fontSize: 13, color: '#888' }}>
          {market.neighborhoodCount} neighborhood{market.neighborhoodCount !== 1 ? 's' : ''}
        </div>
        {market.monthlyPrice != null && (
          <div style={{ fontSize: 15, color: '#333', marginTop: '0.5rem' }}>
            ${market.monthlyPrice.toFixed(2)}
            <span style={{ fontSize: 12, color: '#888' }}>/mo</span>
          </div>
        )}
      </div>
      <div style={{ padding: '0 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {mode === 'active' && (
          <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 600, background: 'rgb(3,123,102)', color: '#fff', padding: '0.3rem 0.9rem', borderRadius: 12, fontFamily: "'Montserrat', sans-serif" }}>
            Active
          </span>
        )}
        {mode === 'subscribe' && (
          <button
            onClick={onSubscribe}
            disabled={checkingOut}
            style={{ width: '100%', padding: '0.6rem', fontSize: 13, fontWeight: 600, background: '#ba9394', color: '#fff', border: 'none', borderRadius: 6, cursor: checkingOut ? 'default' : 'pointer', opacity: checkingOut ? 0.6 : 1, fontFamily: "'Montserrat', sans-serif" }}
          >
            {checkingOut ? 'Loading…' : 'Subscribe'}
          </button>
        )}
      </div>
    </div>
  );
}

interface OfficeTierCardProps {
  market: Market;
  tier: OfficeTier;
  isCurrent: boolean;
  busy: boolean;
  onClick: () => void;
}

function OfficeTierCard({ market, tier, isCurrent, busy, onClick }: OfficeTierCardProps) {
  const photoUrl = market.photoUrl ?? `https://prismassets.blob.core.windows.net/market-images/${market.id}.jpg`;

  return (
    <div style={{
      width: 260,
      border: '1px solid #ddd',
      borderRadius: 10,
      overflow: 'hidden',
      background: isCurrent ? '#fafafa' : '#fff',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        height: 150,
        backgroundImage: `url(${photoUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#e0dbd7',
      }} />
      <div style={{ padding: '1rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        <div style={{ fontWeight: 600, fontSize: 16 }}>{tier.tierName}</div>
        <div style={{ fontSize: 13, color: '#888' }}>{market.name}</div>
        <div style={{ fontSize: 13, color: '#888' }}>{formatSeats(tier.maxUsers)}</div>
        {tier.monthlyPrice != null && (
          <div style={{ fontSize: 15, color: '#333', marginTop: '0.5rem' }}>
            ${tier.monthlyPrice.toFixed(2)}
            <span style={{ fontSize: 12, color: '#888' }}>/mo</span>
          </div>
        )}
      </div>
      <div style={{ padding: '0 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {isCurrent ? (
          <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 600, background: 'rgb(3,123,102)', color: '#fff', padding: '0.3rem 0.9rem', borderRadius: 12, fontFamily: "'Montserrat', sans-serif" }}>
            Active
          </span>
        ) : (
          <button
            onClick={onClick}
            disabled={busy}
            style={{ width: '100%', padding: '0.6rem', fontSize: 13, fontWeight: 600, background: '#ba9394', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: "'Montserrat', sans-serif" }}
          >
            {busy ? 'Loading…' : market.currentOfficeTierId != null ? 'Switch Plan' : 'Subscribe'}
          </button>
        )}
      </div>
    </div>
  );
}

interface OfficeTierSwitchModalProps {
  market: Market;
  tier: OfficeTier;
  activeMemberCount: number;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function OfficeTierSwitchModal({ market, tier, activeMemberCount, busy, onConfirm, onClose }: OfficeTierSwitchModalProps) {
  const currentTier = market.officeTiers.find(t => t.id === market.currentOfficeTierId);
  const isBlocked = tier.maxUsers != null && activeMemberCount > tier.maxUsers;
  const formatSeatLimit = (n: number | null) => (n == null ? 'Unlimited' : `${n}`);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{ width: 400, maxWidth: '90vw', background: '#fff', borderRadius: 10, padding: '1.75rem', ...FONT }}>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 0.75rem' }}>
          Switch {market.name} Office plan?
        </h3>
        <p style={{ fontSize: 14, color: '#444', margin: '0 0 1rem' }}>
          {currentTier ? `${currentTier.tierName} → ` : ''}{tier.tierName}
        </p>
        <div style={{ background: '#f7f5f2', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: 13, color: '#555' }}>
          Seat limit: {formatSeatLimit(currentTier?.maxUsers ?? null)} → {formatSeatLimit(tier.maxUsers)}
        </div>
        {isBlocked ? (
          <p style={{ fontSize: 13, color: '#b3261e', margin: '0 0 1.25rem' }}>
            Your organization currently has {activeMemberCount} active members, which exceeds this plan's limit of {tier.maxUsers}. Remove members or choose a higher tier.
          </p>
        ) : (
          <p style={{ fontSize: 13, color: '#888', margin: '0 0 1.25rem' }}>
            This takes effect immediately and your monthly rate will be prorated for the rest of your billing cycle.
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ padding: '0.55rem 1.1rem', fontSize: 13, fontWeight: 600, background: 'none', color: '#555', border: '1px solid #ddd', borderRadius: 6, cursor: busy ? 'default' : 'pointer', ...FONT }}
          >
            Cancel
          </button>
          {!isBlocked && (
            <button
              onClick={onConfirm}
              disabled={busy}
              style={{ padding: '0.55rem 1.1rem', fontSize: 13, fontWeight: 600, background: '#ba9394', color: '#fff', border: 'none', borderRadius: 6, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, ...FONT }}
            >
              {busy ? 'Switching…' : 'Confirm Switch'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
